import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useLanguage } from "../contexts/LanguageContext";
import { T, SECTIONS_ORDER } from "../i18n/strings";
import { ArticleCard } from "../components/ArticleCard";
import { Link } from "react-router-dom";

export default function Home() {
  const { lang } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles", { params: { limit: 30 } })
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingState />;

  const lead = articles.find((a) => a.is_lead) || articles[0];
  const featured = articles.filter((a) => a.is_featured && a.id !== lead?.id).slice(0, 3);
  const bySection = SECTIONS_ORDER.reduce((acc, s) => {
    acc[s] = articles.filter((a) => a.section === s).slice(0, 3);
    return acc;
  }, {});

  return (
    <div data-testid="home-page">
      {/* Ticker */}
      <div className="bg-[#081422] text-[#FAF8F5] py-2 overflow-hidden border-b border-[#C5A059]/30">
        <div className="pw-ticker-track font-mono-meta text-[0.7rem] uppercase tracking-widest">
          {[...articles.slice(0, 6), ...articles.slice(0, 6)].map((a, i) => (
            <span key={i} className="flex items-center gap-3">
              <span className="text-[#C5A059]">◆</span>
              <span>{a[`title_${lang}`] || a.title_en}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        {/* Hero grid */}
        {lead && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 mb-16">
            <div className="lg:col-span-8">
              <div className="pw-editorial-divider mb-6">
                <span>{T.labels.heroKicker[lang]}</span>
                <span>Nº 01</span>
              </div>
              <ArticleCard article={lead} variant="lead" />
            </div>

            <aside className="lg:col-span-4 space-y-6 lg:border-l lg:border-[#081422]/10 lg:pl-8">
              <div className="pw-editorial-divider">
                <span>{T.labels.featured[lang]}</span>
                <span>{featured.length}</span>
              </div>
              {featured.map((a) => (
                <ArticleCard key={a.id} article={a} variant="compact" />
              ))}
            </aside>
          </div>
        )}

        {/* Section grids */}
        {SECTIONS_ORDER.map((s) => {
          const items = bySection[s];
          if (!items || items.length === 0) return null;
          return (
            <section key={s} className="mb-16" data-testid={`home-section-${s}`}>
              <div className="flex items-end justify-between mb-8 border-b border-[#081422]/15 pb-3">
                <div>
                  <div className="pw-eyebrow">{T.labels.section[lang]}</div>
                  <h2 className="font-serif-display text-3xl lg:text-4xl font-bold text-[#081422] tracking-tight mt-1">
                    {T.nav[s][lang]}
                  </h2>
                </div>
                <Link
                  to={`/section/${s}`}
                  className="pw-link font-mono-meta text-xs uppercase tracking-widest text-[#C5A059] hover:text-[#DAA520]"
                  data-testid={`view-all-${s}`}
                >
                  {T.labels.all[lang]} →
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {items.map((a) => <ArticleCard key={a.id} article={a} />)}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="max-w-7xl mx-auto px-4 py-24 text-center font-mono-meta text-xs uppercase tracking-widest text-[#64748B]" data-testid="loading">
      Loading edition...
    </div>
  );
}
